import { Link } from "react-router-dom";

/** Shared shell for all long-form policy pages — same margins, same rhythm. */
function PolicyLayout({ eyebrow, title, testid, updated, children }) {
  return (
    <main className="max-w-3xl mx-auto px-6 pt-14 pb-20" data-testid={testid}>
      <p className="accent-label text-[#B79256]">{eyebrow}</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2A2823] leading-[1.05] mt-3">
        {title}
      </h1>
      {updated && (
        <p className="text-xs text-[#2A2823]/50 mt-3 accent-label">Last updated: {updated}</p>
      )}
      <div className="mt-8 space-y-5 text-[#2A2823]/80 leading-relaxed text-base">
        {children}
      </div>
      <div className="mt-14 text-sm">
        <Link to="/" className="text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2">
          ← Back to Willoah
        </Link>
      </div>
    </main>
  );
}

export function PrivacyPolicy() {
  return (
    <PolicyLayout eyebrow="privacy" title="Privacy Policy" testid="privacy-policy" updated="February 2026">
      <p>
        Willoah collects information you provide when creating an account, including your
        name, email, and profile photo (via Google sign-in), as well as any content you
        create, such as birthday lists or saved items.
      </p>
      <p>
        We use this information to operate the site and improve your experience.
        We do not sell your personal information.
      </p>
      <p>
        Willoah may use cookies and analytics tools to understand site traffic.
      </p>
      <p>
        By using Willoah, you consent to this policy. For questions, contact{" "}
        <a href="mailto:hello@willoah.com" className="text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2">
          hello@willoah.com
        </a>.
      </p>
    </PolicyLayout>
  );
}

export function TermsOfService() {
  return (
    <PolicyLayout eyebrow="terms" title="Terms of Service" testid="terms-of-service" updated="February 2026">
      <p>
        By using Willoah, you agree to use the site respectfully and lawfully.
      </p>
      <p>
        Content you create (lists, notes, reviews) remains yours, but you grant Willoah
        permission to display it on the platform.
      </p>
      <p>
        Willoah is not responsible for the accuracy of third-party products, links, or
        retailer information shared through the site.
      </p>
      <p>
        We may update these terms as Willoah grows; continued use of the site means you
        accept any changes.
      </p>
      <p>
        Questions? Reach us at{" "}
        <a href="mailto:hello@willoah.com" className="text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2">
          hello@willoah.com
        </a>.
      </p>
    </PolicyLayout>
  );
}

export function AffiliateDisclosure() {
  return (
    <PolicyLayout eyebrow="affiliate disclosure" title="Affiliate Disclosure" testid="affiliate-disclosure" updated="February 2026">
      <p>
        Willoah keeps The Grove chronological — newest first. Sponsored content may appear on
        Willoah, but it is always clearly labeled so you know exactly what you're looking at.
        Brands can never buy a positive review or the review's removal.
      </p>
      <p>
        On non-Trusted branches (Ideas, Recipes, Lifestyle, Faith), the person who
        planted the branch can add a <b>Shop This</b> list — the specific items they
        actually used. Any link in that list may be the sharer's own affiliate link,
        which means they might earn a small commission if you buy through it. Every
        Shop This block on the site carries an <em>"Affiliate links"</em> label so this
        disclosure is automatic and unmissable — not tucked into paragraph text.
      </p>
      <p>
        <b>Willoah itself takes no cut</b> of any affiliate purchase. The company doesn't
        add affiliate tags to the sharer's links, doesn't intercept them, and doesn't
        earn a referral fee.
      </p>
      <p>
        <b>Trusted branches never carry affiliate links.</b> Trusted is for local people, places,
        and services a real person would personally recommend — a dentist, a restaurant, a
        daycare, a tutor — and the category is intentionally walled off from any monetized
        outbound link. The Shop This feature is disabled on Trusted branches at the server
        level, not just in the UI.
      </p>
      <p>
        The one commercial channel Willoah operates is <Link to="/try-and-critique" className="text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2">Try &amp; Critique</Link>{" "}
        — a flat fee brands can pay to enter a pool of products offered to real users
        for honest review. The fee only buys a fair shot at being tested; it does not
        buy a positive review, cannot remove a negative one, and never influences The
        Grove's ordering.
      </p>
      <p>
        Questions about how a specific branch handles affiliate links?{" "}
        <a href="mailto:hello@willoah.com" className="text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2">
          hello@willoah.com
        </a>.
      </p>
    </PolicyLayout>
  );
}
